import qs from 'qs';
import { captureException } from '@sentry/nextjs';
import { v4 as uuid } from 'uuid';
import {
  FETCH_SEARCH_POSTS_RESULTS,
  FETCH_SEARCH_POSTS_RESULTS_SUCCESS,
  FETCH_SEARCH_POSTS_RESULTS_FAILURE,
  FETCH_SEARCH_COMMUNITIES_RESULTS,
  FETCH_SEARCH_COMMUNITIES_RESULTS_SUCCESS,
  FETCH_SEARCH_COMMUNITIES_RESULTS_FAILURE,
  FETCH_SEARCH_MEMBERS_RESULTS,
  FETCH_SEARCH_MEMBERS_RESULTS_SUCCESS,
  FETCH_SEARCH_MEMBERS_RESULTS_FAILURE,
  RESET_SEARCH,
} from 'src/modules/search/shared/actionTypes';
import api from 'src/utils/api';
import { trackFootfallEvent } from 'src/utils/footfall';
import { userIsCommunityMemberOrHUStaff } from 'src/utils/context';
import { Event } from 'src/constants/footfall';
import paginationResultsPerPage from 'src/constants/paginationResultsPerPage';
import { setAlert } from 'src/components/alert/actions';
import { SortType } from 'src/constants/types';

type Params = {
  q: string;
  start?: number;
  ['community-id']?: string | number;
  ['community-ids']?: string[];
  lang?: string;
  limit?: number;
}
export function fetchSearchPosts(
  query: string,
  currentPage: string,
  communityFilter: string | number,
  sortBy: SortType,
  usePrivateEndpointForMembers?: boolean,
) {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;
    dispatch({
      type: FETCH_SEARCH_POSTS_RESULTS,
      query,
      currentPage,
      communityFilter,
      searchId,
    });
    if (!query) return;

    try {
      const isSortByDate = sortBy === 'newest';
      const limit = paginationResultsPerPage.search;
      const start = limit * (Number(currentPage) - 1);
      const { context: { lang, currentUser } } = getState();
      const currentUserId = currentUser?.userId;
      const isMember = typeof (communityFilter) === 'number' && userIsCommunityMemberOrHUStaff({ currentUser, communityId: communityFilter });

      let communityFilterArray;

      if (communityFilter === 'all') {
        communityFilterArray = [];
      } else if (communityFilter === 'my') {
        communityFilterArray = currentUser === null
          ? []
          : currentUser.followedCommunities.map(x => x.communityId);
      } else {
        communityFilterArray = [Number(communityFilter)];
      }

      const params: Params = {
        q: query,
      };

      if (start !== 0) {
        params.start = start;
      }

      if (communityFilter !== 'all') {
        if (usePrivateEndpointForMembers && isMember) {
          params['community-id'] = communityFilter;
        } else {
          params['community-ids'] = communityFilterArray;
        }
      }

      if (isSortByDate) {
        // default sort is by relevance
        params['sort-by'] = 'date';
      }

      if (lang !== 'en') {
        params.lang = lang;
      }

      const requestParams = {
        'params': params,
        'paramsSerializer': params => qs.stringify(params, { arrayFormat: 'repeat' }),
      };

      const response = usePrivateEndpointForMembers && isMember
        ? await api.get(`private/search/${currentUserId}/posts`, requestParams)
        : await api.getPublic('search/posts', requestParams);

      const { data: { posts, communityAggregates, total } } = response;

      const footfallQuery = total === 0 ? `no-results:${query}` : query;
      if (communityFilter && communityFilter !== 'all') {
        trackFootfallEvent(Event.SEARCHED_COMMUNITY, { searchQuery: footfallQuery, communityPath: communityFilter, searchId, searchType: 'posts' });
      } else {
        trackFootfallEvent(Event.SEARCHED_SITE, { searchQuery: footfallQuery, searchType: 'posts', searchId });
      }

      dispatch({
        type: FETCH_SEARCH_POSTS_RESULTS_SUCCESS,
        totalResults: total,
        results: posts,
        communityFilterOptions: communityAggregates,
      });

    } catch (ex) {
      captureException(ex);
      dispatch({
        type: FETCH_SEARCH_POSTS_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
}

export const fetchSearchCommunities = (query: string, currentPage: string) => {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;
    dispatch({
      type: FETCH_SEARCH_COMMUNITIES_RESULTS,
      currentPage,
      query,
      searchId,
    });
    if (!query) return;
    try {
      const lang = getState().context.lang;
      const limit = paginationResultsPerPage.searchCommunities;
      const start = limit * (Number(currentPage) - 1);

      const params: Params = {
        q: query,
      };

      if (lang !== 'en') {
        params.lang = lang;
      }

      if (start !== 0) {
        params.start = start;
      }

      const response = await api.getPublic('search/communities',
        {
          'params': params,
        },
      );
      const { data: { communities, total } } = response;

      const footfallQuery = total === 0 ? `no-results:${query}` : query;
      trackFootfallEvent(Event.SEARCHED_SITE, { searchQuery: footfallQuery, searchType: 'communities', searchId });

      dispatch({
        type: FETCH_SEARCH_COMMUNITIES_RESULTS_SUCCESS,
        totalResults: total,
        results: communities,
      });

    } catch (ex) {
      captureException(ex);
      dispatch({
        type: FETCH_SEARCH_COMMUNITIES_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchSearchMembers = (query: string, currentPage: string, communityFilter: string | number) => {
  return async (dispatch, getState) => {
    const { pages: { search: { searchQuery, searchId: currentSearchId } } } = getState();
    const searchId = searchQuery !== query ? uuid() : currentSearchId;

    dispatch({
      type: FETCH_SEARCH_MEMBERS_RESULTS,
      query,
      currentPage,
      communityFilter,
      searchId,
    });

    if (!query) return;

    try {
      if (communityFilter && communityFilter !== 'all') {
        trackFootfallEvent(Event.SEARCHED_COMMUNITY, { searchQuery: query, communityPath: communityFilter, searchId, searchType: 'members' });
      }

      const limit = paginationResultsPerPage.searchMembers;
      const start = limit * (Number(currentPage) - 1);
      const { context: { lang, currentUser } } = getState();
      const currentUserId = currentUser && currentUser.userId;

      const params: Params = {
        q: query,
        'community-id': communityFilter,
        limit,
      };

      if (start !== 0) {
        params.start = start;
      }

      if (lang !== 'en') {
        params.lang = lang;
      }

      const requestParams = {
        'params': params,
        'paramsSerializer': params => qs.stringify(params, { arrayFormat: 'repeat' }),
      };

      const response = await api.get(`private/search/${currentUserId}/members`, requestParams);
      const { data: { members, total } } = response;

      const membersResults = members.map(member => ({ ...member, roles: member.roles.map(role => role.role) }));

      dispatch({
        type: FETCH_SEARCH_MEMBERS_RESULTS_SUCCESS,
        membersResults,
        totalResults: total,
      });

    } catch (ex) {
      captureException(ex);
      dispatch({
        type: FETCH_SEARCH_MEMBERS_RESULTS_FAILURE,
      });
      dispatch(setAlert(ex.message));
    }
  };
};

export const resetSearch = () => ({ type: RESET_SEARCH });
